import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function ServicesSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const services = [
    {
      icon: 'ri-plane-line',
      title: 'Transferts Aéroport',
      description: 'Genève, Lyon, Chambéry, Grenoble vers toutes les stations alpines',
      features: ['Suivi des vols', 'Attente incluse', 'Accueil personnalisé']
    },
    {
      icon: 'ri-train-line',
      title: 'Transferts Gare',
      description: 'Depuis les gares TGV vers votre station de ski',
      features: ['Moûtiers', 'Annecy', 'Chambéry']
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Transferts Sur-Mesure',
      description: 'Trajets personnalisés selon vos besoins',
      features: ['Itinéraire flexible', 'Arrêts multiples', 'Disponible 24/7']
    },
    {
      icon: 'ri-group-line',
      title: 'Groupes & Événements',
      description: 'Transport pour groupes et événements spéciaux',
      features: ['Jusqu\'à 7 passagers', 'Mercedes Van', 'Tarifs groupes']
    }
  ];

  return (
    <section ref={ref} className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-voyage-gold/10 px-4 py-2 rounded-full mb-6"
          >
            <i className="ri-service-line text-voyage-gold"></i>
            <span className="text-night-blue font-inter text-sm font-semibold">Nos Services</span>
          </motion.div>
          
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-night-blue mb-4">
            Services de Transfert Premium
          </h2>
          <p className="text-slate-600 font-inter text-base sm:text-lg max-w-2xl mx-auto">
            Des solutions de transport adaptées à tous vos besoins
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8 rounded-2xl border-2 border-slate-200 hover:border-voyage-gold hover:shadow-2xl transition-all cursor-default group"
            >
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="w-16 h-16 bg-voyage-gold/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-voyage-gold/20 transition-colors"
              >
                <i className={`${service.icon} text-voyage-gold text-3xl`}></i>
              </motion.div>

              <h3 className="font-playfair text-xl sm:text-2xl font-bold text-night-blue mb-3 group-hover:text-voyage-gold transition-colors">
                {service.title}
              </h3>
              
              <p className="text-slate-600 font-inter text-sm sm:text-base mb-4 leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                    transition={{ duration: 0.4, delay: index * 0.1 + idx * 0.1 }}
                    className="flex items-center gap-2 text-slate-700 font-inter text-sm"
                  >
                    <i className="ri-check-line text-voyage-gold"></i>
                    <span>{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <motion.a
            href="/services"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-night-blue text-white rounded-full font-inter font-semibold text-base hover:bg-voyage-gold hover:text-night-blue transition-all shadow-lg whitespace-nowrap cursor-pointer group"
          >
            Voir tous nos services
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
